import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ApiError } from '@estudio/contracts';
import { WorkspaceSync } from '@estudio/cloud-client';
import type { AuthSession } from '@estudio/desktop-contract';
import { useServices } from './services.tsx';
import { Button, ErrorState, Loading, Notice } from '../ui/index.tsx';
import { LoginPage } from '../app/auth/LoginPage.tsx';

export interface WorkspaceInfo {
  id: string;
  name: string;
  role: 'viewer' | 'editor' | 'owner';
  timezone: string;
  status: string;
}

interface SessionValue {
  displayName: string;
  activateProfile:()=>void;
  session: AuthSession;
  workspace: WorkspaceInfo;
  workspaces: WorkspaceInfo[];
  signOut: () => Promise<void>;
  chooseWorkspace: () => void;
  sync: WorkspaceSync;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('Sesión no disponible');
  return v;
}

export function useWorkspaceId(): string {
  return useSession().workspace.id;
}

/**
 * Puerta de sesión: sin sesión muestra el acceso por código; con sesión asegura
 * el espacio del piloto y arranca la sincronización incremental de cambios.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { auth } = useServices();
  const [session, setSession] = useState<AuthSession | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const hadSession = useRef(false);

  useEffect(() => {
    let alive = true;
    let eventVersion = 0;
    let checking = false;
    const accept = (s: AuthSession | null) => { if (s) hadSession.current = true; setError(null); setSession(s); };
    const off = auth.onChange((s) => { eventVersion++; if (alive) accept(s); });
    const check = () => {
      if (checking) return;
      checking = true;
      const version = eventVersion;
      void auth.getSession().then(s => { if (alive && version === eventVersion) accept(s); })
        .catch(e => { if (alive && version === eventVersion) setError(e); })
        .finally(() => { checking = false; });
    };
    check();
    window.addEventListener('focus', check); window.addEventListener('online', check);
    return () => {
      alive = false;
      off();
      window.removeEventListener('focus', check); window.removeEventListener('online', check);
    };
  }, [auth, attempt]);

  useEffect(() => {
    if (session === null) void window.__ESTUDIO_DESKTOP__?.host?.clearWorkspace().catch(() => undefined);
  }, [session]);

  if (error && session === undefined) return <div className="content"><ErrorState error={error} title="No se pudo comprobar la sesión" onRetry={() => setAttempt(n => n + 1)} /></div>;
  if (session === undefined) return <div className="content"><Loading label="Comprobando sesión…" /></div>;
  if (session === null) return <LoginPage sessionNotice={hadSession.current ? 'Tu sesión ya no está disponible. Vuelve a entrar; tus borradores locales guardados se conservan en este equipo.' : undefined} />;
  // Remount before painting another account: previous documents never flash on screen.
  return <>{error && <Notice tone="warn">No se pudo comprobar tu sesión. Tus borradores se conservan. <Button onClick={() => setAttempt(n => n + 1)}>Volver a comprobar</Button></Notice>}<WorkspaceGate key={session.userId} session={session}>{children}</WorkspaceGate></>;
}

function WorkspaceGate({ session, children }: { session: AuthSession; children: ReactNode }) {
  const { auth, client } = useServices();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setError(null);
    client
      .call('workspace.bootstrap', {})
      .then((r) => {
        if (!alive) return;
        const spaces = r.workspaces as WorkspaceInfo[];
        setWorkspaces(spaces);
        // Multiple memberships require an explicit choice, never the first array item.
        setSelectedId(spaces.length === 1 ? spaces[0]!.id : null);
      })
      .catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, [client, attempt]);

  if (error) {
    return (
      <div className="content stack">
        <ErrorState error={error} onRetry={() => setAttempt((a) => a + 1)} title="No se pudo abrir tu espacio" />
        <Button onClick={() => void auth.signOut()}>Cerrar sesión</Button>
      </div>
    );
  }
  if (!workspaces) return <div className="content"><Loading label="Abriendo tu espacio…" /></div>;
  const workspace = workspaces.find((w) => w.id === selectedId);
  if (!workspace) return <main className="content stack" style={{ maxWidth: 640, margin: '10vh auto' }}>
    <h1>{workspaces.length ? 'Elige tu espacio' : 'No tienes espacios disponibles'}</h1>
    {workspaces.map((w) => <Button key={w.id} onClick={() => setSelectedId(w.id)}>{w.name} · {w.role}</Button>)}
    <Button onClick={() => void auth.signOut()}>Cerrar sesión</Button>
  </main>;
  return <BoundWorkspace key={workspace.id} session={session} workspace={workspace} workspaces={workspaces}
    chooseWorkspace={() => { navigate('/inicio', { replace: true }); setSelectedId(null); }}>{children}</BoundWorkspace>;
}

interface BoundProps { session: AuthSession; workspace: WorkspaceInfo; workspaces: WorkspaceInfo[]; chooseWorkspace: () => void; children: ReactNode }

function BoundWorkspace(props: BoundProps) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  // Separate cache objects, including mutations, for each account/workspace lifetime.
  const [queries] = useState(() => new QueryClient({ defaultOptions: {
    queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: (count, e) => e instanceof ApiError ? e.retryable && count < 2 : count < 2 },
    mutations: { retry: false },
  } }));
  useEffect(() => {
    let alive = true;
    const host = window.__ESTUDIO_DESKTOP__?.host;
    setReady(false); setError(null);
    void (async () => {
      await host?.clearWorkspace();
      if (!alive) return;
      if (alive) setReady(true);
    })().catch((e) => { if (alive) setError(e); });
    return () => {
      alive = false;
      void queries.cancelQueries(); queries.clear();
      void host?.clearWorkspace().catch(() => undefined);
    };
  }, [props.workspace.id, attempt, queries]);
  if (error) return <div className="content stack"><ErrorState error={error} title="No se pudo abrir este espacio" onRetry={() => setAttempt((n) => n + 1)} /><Button onClick={props.chooseWorkspace}>Elegir otro espacio</Button></div>;
  if (!ready) return <div className="content"><Loading label="Abriendo tu espacio…" /></div>;
  return <QueryClientProvider client={queries}><SessionReady {...props} /></QueryClientProvider>;
}

function SessionReady({ session, workspace, workspaces, chooseWorkspace, children }: BoundProps) {
  const { auth, client } = useServices();
  const qc = useQueryClient();
  const [profileEnabled,setProfileEnabled]=useState(false);
  const activateProfile=useCallback(()=>setProfileEnabled(true),[]);
  const profile = useQuery({queryKey:['op','workspace.account_get',{workspace_id:workspace.id}],queryFn:()=>client.call('workspace.account_get',{workspace_id:workspace.id}),refetchInterval:60_000,enabled:profileEnabled});
  const current = useQuery({queryKey:['op','workspace.get',{workspace_id:workspace.id}],queryFn:()=>client.call('workspace.get',{workspace_id:workspace.id}),refetchInterval:60_000,enabled:profileEnabled});
  const liveWorkspace = useMemo(()=>({...workspace, ...(current.data?.workspace ?? {})}),[workspace,current.data]);
  const displayName = profile.data?.display_name ?? liveWorkspace.name;
  const sync = useMemo(() => new WorkspaceSync(client, workspace.id), [client, workspace.id]);

  const value = useMemo<SessionValue>(
    () => ({ session, displayName, activateProfile, workspace:liveWorkspace, workspaces, sync, chooseWorkspace, signOut: async () => {
      await window.__ESTUDIO_DESKTOP__?.host?.clearWorkspace();
      await auth.signOut(); qc.clear();
    } }),
    [session, displayName, activateProfile, liveWorkspace, workspaces, sync, chooseWorkspace, auth, qc],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Mounted only after the current legal decisions are confirmed. */
export function WorkspaceRuntime({children}:{children:ReactNode}) {
  const {session,workspace,sync,chooseWorkspace,activateProfile}=useSession();
  const qc=useQueryClient();
  const [ready,setReady]=useState(false),[error,setError]=useState<unknown>(null),[attempt,setAttempt]=useState(0);
  const boundBearer=useRef<string | null>(null);
  const [bearerError,setBearerError]=useState(false),[bearerAttempt,setBearerAttempt]=useState(0);
  useEffect(()=>{
    let alive=true;
    const host=window.__ESTUDIO_DESKTOP__?.host;
    let stop=()=>{};
    setReady(false);setError(null);boundBearer.current=null;
    void(async()=>{
      await host?.bindWorkspace(workspace.id,session.accessToken);
      if(!alive)return;
      boundBearer.current=session.accessToken;
      const poll=()=>void sync.poll().catch(()=>undefined);
      const off=sync.onChange(()=>void qc.invalidateQueries({queryKey:['op']}));
      poll();window.addEventListener('focus',poll);window.addEventListener('online',poll);
      const timer=setInterval(poll,60_000);
      stop=()=>{off();window.removeEventListener('focus',poll);window.removeEventListener('online',poll);clearInterval(timer);};
      activateProfile();
      setReady(true);
    })().catch(e=>{if(alive)setError(e);});
    return()=>{alive=false;stop();void host?.clearWorkspace().catch(()=>undefined);};
    // A refreshed access token does not restart authorized local work.
  },[workspace.id,sync,qc,attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    const host=window.__ESTUDIO_DESKTOP__?.host;
    if(!ready || !host || boundBearer.current===session.accessToken)return;
    let alive=true;
    void host.bindWorkspace(workspace.id,session.accessToken).then(()=>{
      if(alive){boundBearer.current=session.accessToken;setBearerError(false);}
    }).catch(()=>{if(alive)setBearerError(true);});
    return()=>{alive=false;};
  },[ready,workspace.id,session.accessToken,bearerAttempt]);
  useEffect(()=>{
    if(!bearerError)return;
    const retry=()=>setBearerAttempt(n=>n+1);
    window.addEventListener('focus',retry);window.addEventListener('online',retry);
    return()=>{window.removeEventListener('focus',retry);window.removeEventListener('online',retry);};
  },[bearerError]);
  if(error)return <div className="content stack"><ErrorState error={error} title="No se pudo activar el equipo" onRetry={()=>setAttempt(n=>n+1)}/><Button onClick={chooseWorkspace}>Elegir otro espacio</Button></div>;
  if(!ready)return <div className="content"><Loading label="Activando tu espacio…"/></div>;
  return <>{bearerError && <Notice tone="warn">No se pudo actualizar el acceso del escritorio. Tus borradores se conservan. <Button onClick={()=>setBearerAttempt(n=>n+1)}>Reintentar acceso del escritorio</Button></Notice>}{children}</>;
}
